/* eslint-disable react-refresh/only-export-components -- intentional: shared module co-exports helpers with components */
import React from 'react';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';

/* ── animations ── */
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const Svg = styled.svg<{ $pulse?: boolean; $spin?: boolean }>`
  flex-shrink: 0;
  transform-origin: center;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.08));
  ${({ $pulse }) => $pulse && css`animation: ${pulse} 2s ease-in-out infinite;`}
  ${({ $spin }) => $spin && css`animation: ${spin} 2s linear infinite;`}
`;

/* ── types ── */
type IconType =
  | 'backlog' | 'open' | 'todo' | 'in-progress' | 'writing' | 'review'
  | 'testing' | 'done' | 'cancelled' | 'blocked' | 'docs' | 'deploy'
  | 'design' | 'analysis' | 'approval' | 'planning' | 'integration' | 'default';

type StatusDef = {
  color: string;
  bg: string;        // lighter fill for background
  icon: IconType;
  pulse?: boolean;
  spin?: boolean;
};

const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

/* ── status map ── */
const statusMap: [string, StatusDef][] = [
  // Бэклог / Backlog
  ['бэклог',           { color: '#9ca3af', bg: '#f3f4f6', icon: 'backlog' }],
  ['backlog',          { color: '#9ca3af', bg: '#f3f4f6', icon: 'backlog' }],
  // Открыт / Open
  ['открыт',           { color: '#3b82f6', bg: '#dbeafe', icon: 'open' }],
  ['открыто',          { color: '#3b82f6', bg: '#dbeafe', icon: 'open' }],
  ['open',             { color: '#3b82f6', bg: '#dbeafe', icon: 'open' }],
  ['новый',            { color: '#3b82f6', bg: '#dbeafe', icon: 'open' }],
  ['new',              { color: '#3b82f6', bg: '#dbeafe', icon: 'open' }],
  // К выполнению / Todo
  ['к выполнению',     { color: '#6366f1', bg: '#e0e7ff', icon: 'todo' }],
  ['todo',             { color: '#6366f1', bg: '#e0e7ff', icon: 'todo' }],
  ['to do',            { color: '#6366f1', bg: '#e0e7ff', icon: 'todo' }],
  // Планирование / Planning
  ['планирование',     { color: '#8b5cf6', bg: '#ede9fe', icon: 'planning' }],
  ['план',             { color: '#8b5cf6', bg: '#ede9fe', icon: 'planning' }],
  ['planning',         { color: '#8b5cf6', bg: '#ede9fe', icon: 'planning' }],
  // В работе / In Progress
  ['в работе',         { color: '#f59e0b', bg: '#fef3c7', icon: 'in-progress' }],
  ['in progress',      { color: '#f59e0b', bg: '#fef3c7', icon: 'in-progress' }],
  // Writing
  ['writing',          { color: '#8b5cf6', bg: '#ede9fe', icon: 'writing', pulse: true }],
  ['написание',        { color: '#8b5cf6', bg: '#ede9fe', icon: 'writing', pulse: true }],
  // Дизайн / Design
  ['дизайн',           { color: '#ec4899', bg: '#fce7f3', icon: 'design' }],
  ['design',           { color: '#ec4899', bg: '#fce7f3', icon: 'design' }],
  ['ui',               { color: '#ec4899', bg: '#fce7f3', icon: 'design' }],
  ['ux',               { color: '#ec4899', bg: '#fce7f3', icon: 'design' }],
  // Аналитика / Analysis
  ['аналитика',        { color: '#f97316', bg: '#ffedd5', icon: 'analysis' }],
  ['анализ',           { color: '#f97316', bg: '#ffedd5', icon: 'analysis' }],
  ['analysis',         { color: '#f97316', bg: '#ffedd5', icon: 'analysis' }],
  // На ревью / Review
  ['на ревью',         { color: '#a855f7', bg: '#f3e8ff', icon: 'review' }],
  ['review',           { color: '#a855f7', bg: '#f3e8ff', icon: 'review' }],
  ['ревью',            { color: '#a855f7', bg: '#f3e8ff', icon: 'review' }],
  ['код-ревью',        { color: '#a855f7', bg: '#f3e8ff', icon: 'review' }],
  ['code review',      { color: '#a855f7', bg: '#f3e8ff', icon: 'review' }],
  // Тестирование / Testing
  ['тестирование',     { color: '#e879f9', bg: '#fae8ff', icon: 'testing' }],
  ['testing',          { color: '#e879f9', bg: '#fae8ff', icon: 'testing' }],
  ['тест',             { color: '#e879f9', bg: '#fae8ff', icon: 'testing' }],
  ['qa',               { color: '#e879f9', bg: '#fae8ff', icon: 'testing' }],
  // Согласование / Approval
  ['согласование',     { color: '#eab308', bg: '#fef9c3', icon: 'approval' }],
  ['approval',         { color: '#eab308', bg: '#fef9c3', icon: 'approval' }],
  ['утверждение',      { color: '#eab308', bg: '#fef9c3', icon: 'approval' }],
  // Документирование / Docs
  ['документирование', { color: '#0ea5e9', bg: '#e0f2fe', icon: 'docs' }],
  ['документация',     { color: '#0ea5e9', bg: '#e0f2fe', icon: 'docs' }],
  ['docs',             { color: '#0ea5e9', bg: '#e0f2fe', icon: 'docs' }],
  ['documentation',    { color: '#0ea5e9', bg: '#e0f2fe', icon: 'docs' }],
  // Интеграция / Integration
  ['интеграция',       { color: '#14b8a6', bg: '#ccfbf1', icon: 'integration' }],
  ['integration',      { color: '#14b8a6', bg: '#ccfbf1', icon: 'integration' }],
  // Деплой / Deploy
  ['деплой',           { color: '#22c55e', bg: '#dcfce7', icon: 'deploy' }],
  ['deploy',           { color: '#22c55e', bg: '#dcfce7', icon: 'deploy' }],
  ['релиз',            { color: '#22c55e', bg: '#dcfce7', icon: 'deploy' }],
  ['release',          { color: '#22c55e', bg: '#dcfce7', icon: 'deploy' }],
  // Готово / Done
  ['готово',           { color: '#10b981', bg: '#d1fae5', icon: 'done' }],
  ['done',             { color: '#10b981', bg: '#d1fae5', icon: 'done' }],
  ['завершено',        { color: '#10b981', bg: '#d1fae5', icon: 'done' }],
  ['закрыт',           { color: '#10b981', bg: '#d1fae5', icon: 'done' }],
  ['closed',           { color: '#10b981', bg: '#d1fae5', icon: 'done' }],
  // Отменено / Cancelled
  ['отменено',         { color: '#9ca3af', bg: '#f3f4f6', icon: 'cancelled' }],
  ['cancelled',        { color: '#9ca3af', bg: '#f3f4f6', icon: 'cancelled' }],
  ['canceled',         { color: '#9ca3af', bg: '#f3f4f6', icon: 'cancelled' }],
  // Заблокировано / Blocked
  ['заблокировано',    { color: '#ef4444', bg: '#fee2e2', icon: 'blocked' }],
  ['blocked',          { color: '#ef4444', bg: '#fee2e2', icon: 'blocked' }],
];

const defaultStatus: StatusDef = { color: '#6366f1', bg: '#e0e7ff', icon: 'default' };

const findStatus = (status: string): StatusDef => {
  const key = normalize(status);
  for (const [name, def] of statusMap) {
    if (key === name) return def;
  }
  for (const [name, def] of statusMap) {
    if (key.includes(name) || name.includes(key)) return def;
  }
  return defaultStatus;
};

/* ── icon renderer ── */
const renderIcon = (icon: IconType, color: string, bg: string, s: number, uid: string) => {
  const cx = s / 2;
  const cy = s / 2;
  const r = s / 2 - 1;
  const sw = Math.max(1.4, s / 9);

  switch (icon) {
    /* ── Backlog: dashed circle, muted ── */
    case 'backlog':
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw}
            strokeDasharray={`${r * 0.8} ${r * 0.55}`} opacity={0.6} />
        </>
      );

    /* ── Open: circle with bright ring pulse ── */
    case 'open':
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw} />
          <circle cx={cx} cy={cy} r={r * 0.45} fill="none" stroke={color} strokeWidth={sw * 0.9}
            strokeDasharray={`${r * 0.65} ${r * 0.4}`} />
        </>
      );

    /* ── Todo: clean circle with center dot ── */
    case 'todo':
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw} />
          <circle cx={cx} cy={cy} r={r * 0.22} fill={color} />
        </>
      );

    /* ── Planning: calendar grid ── */
    case 'planning': {
      const p = s * 0.24; // padding
      const w = s - p * 2;
      const h = w;
      const hdrH = h * 0.3;
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw * 0.5} opacity={0.3} />
          <rect x={p} y={p + s * 0.03} width={w} height={h - s * 0.03} rx={sw} fill="none" stroke={color} strokeWidth={sw * 0.9} />
          <rect x={p} y={p + s * 0.03} width={w} height={hdrH} rx={sw} fill={color} opacity={0.25} />
          <line x1={p} y1={p + s * 0.03 + hdrH} x2={p + w} y2={p + s * 0.03 + hdrH} stroke={color} strokeWidth={sw * 0.6} />
          <circle cx={cx - w * 0.18} cy={p + hdrH + (h - hdrH) * 0.5 + s * 0.02} r={sw * 0.5} fill={color} />
          <circle cx={cx + w * 0.18} cy={p + hdrH + (h - hdrH) * 0.5 + s * 0.02} r={sw * 0.5} fill={color} />
        </>
      );
    }

    /* ── In Progress: arc sweep (spinning) ── */
    case 'in-progress': {
      const ar = r * 0.62;
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw} opacity={0.2} />
          {/* 270-degree arc */}
          <path
            d={`M${cx},${cy - ar} A${ar},${ar} 0 1,1 ${cx - ar},${cy}`}
            fill="none" stroke={color} strokeWidth={sw * 1.4} strokeLinecap="round"
          />
          {/* Arrowhead */}
          <path
            d={`M${cx - ar - ar * 0.32},${cy - ar * 0.18} L${cx - ar},${cy} L${cx - ar + ar * 0.22},${cy - ar * 0.32}`}
            fill="none" stroke={color} strokeWidth={sw * 1.1} strokeLinecap="round" strokeLinejoin="round"
          />
        </>
      );
    }

    /* ── Writing: pencil on paper ── */
    case 'writing': {
      const o = s * 0.28; // offset
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw * 0.5} opacity={0.3} />
          <path
            d={`M${o + s * 0.04},${s - o - s * 0.02} L${s - o - s * 0.02},${o + s * 0.04} L${s - o + s * 0.02},${o + s * 0.08} L${o + s * 0.08},${s - o + s * 0.02} Z`}
            fill={color} opacity={0.2} stroke={color} strokeWidth={sw * 0.8} strokeLinejoin="round"
          />
          <line x1={o + s * 0.02} y1={s - o + s * 0.04} x2={o + s * 0.16} y2={s - o + s * 0.04}
            stroke={color} strokeWidth={sw} strokeLinecap="round" />
        </>
      );
    }

    /* ── Design: bezier pen tool ── */
    case 'design': {
      const m = s * 0.26;
      const t = s * 0.74;
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw * 0.5} opacity={0.3} />
          <path
            d={`M${m},${t} C${m},${cy - s * 0.08} ${t},${cy + s * 0.08} ${t},${m}`}
            fill="none" stroke={color} strokeWidth={sw * 1.1} strokeLinecap="round"
          />
          <circle cx={m} cy={t} r={sw * 1.1} fill={color} />
          <circle cx={t} cy={m} r={sw * 1.1} fill={color} />
          {/* control line hints */}
          <line x1={m} y1={t} x2={m} y2={cy - s * 0.04} stroke={color} strokeWidth={sw * 0.5} opacity={0.4} strokeDasharray="2 2" />
          <line x1={t} y1={m} x2={t} y2={cy + s * 0.04} stroke={color} strokeWidth={sw * 0.5} opacity={0.4} strokeDasharray="2 2" />
        </>
      );
    }

    /* ── Analysis: magnifying glass ── */
    case 'analysis': {
      const gr = r * 0.36;
      const gx = cx - r * 0.1;
      const gy = cy - r * 0.1;
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw * 0.5} opacity={0.3} />
          <circle cx={gx} cy={gy} r={gr} fill={color} opacity={0.15} />
          <circle cx={gx} cy={gy} r={gr} fill="none" stroke={color} strokeWidth={sw * 1.1} />
          <line
            x1={gx + gr * 0.65} y1={gy + gr * 0.65}
            x2={cx + r * 0.55} y2={cy + r * 0.55}
            stroke={color} strokeWidth={sw * 1.5} strokeLinecap="round"
          />
        </>
      );
    }

    /* ── Review: eye ── */
    case 'review': {
      const ew = r * 0.75;
      const eh = r * 0.48;
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw * 0.5} opacity={0.3} />
          {/* Eye shape - filled */}
          <path
            d={`M${cx - ew},${cy} Q${cx},${cy - eh} ${cx + ew},${cy} Q${cx},${cy + eh} ${cx - ew},${cy} Z`}
            fill={color} opacity={0.15} stroke={color} strokeWidth={sw * 0.9}
          />
          {/* Iris */}
          <circle cx={cx} cy={cy} r={r * 0.2} fill={color} />
          {/* Pupil highlight */}
          <circle cx={cx + r * 0.06} cy={cy - r * 0.06} r={r * 0.06} fill="white" opacity={0.7} />
        </>
      );
    }

    /* ── Testing: flask with bubbles ── */
    case 'testing': {
      const tw = r * 0.32;
      const top = cy - r * 0.55;
      const bot = cy + r * 0.65;
      const mid = cy - r * 0.05;
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw * 0.5} opacity={0.3} />
          {/* Flask body fill */}
          <path
            d={`M${cx - tw},${mid} L${cx - r * 0.52},${bot} L${cx + r * 0.52},${bot} L${cx + tw},${mid} Z`}
            fill={color} opacity={0.15}
          />
          {/* Flask outline */}
          <path
            d={`M${cx - tw},${top} L${cx - tw},${mid} L${cx - r * 0.52},${bot} L${cx + r * 0.52},${bot} L${cx + tw},${mid} L${cx + tw},${top}`}
            fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
          />
          <line x1={cx - tw - r * 0.08} y1={top} x2={cx + tw + r * 0.08} y2={top}
            stroke={color} strokeWidth={sw} strokeLinecap="round" />
          {/* Bubbles */}
          <circle cx={cx - r * 0.12} cy={cy + r * 0.25} r={sw * 0.6} fill={color} opacity={0.5} />
          <circle cx={cx + r * 0.15} cy={cy + r * 0.15} r={sw * 0.4} fill={color} opacity={0.4} />
        </>
      );
    }

    /* ── Approval: shield with check ── */
    case 'approval': {
      const sh = r * 0.65;
      const sw2 = r * 0.48;
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw * 0.5} opacity={0.3} />
          {/* Shield */}
          <path
            d={`M${cx},${cy - sh} L${cx + sw2},${cy - sh * 0.55} L${cx + sw2},${cy + sh * 0.15} Q${cx},${cy + sh} ${cx - sw2},${cy + sh * 0.15} L${cx - sw2},${cy - sh * 0.55} Z`}
            fill={color} opacity={0.18} stroke={color} strokeWidth={sw * 0.8} strokeLinejoin="round"
          />
          {/* Checkmark inside */}
          <path
            d={`M${cx - r * 0.18},${cy} L${cx - r * 0.02},${cy + r * 0.18} L${cx + r * 0.22},${cy - r * 0.15}`}
            fill="none" stroke={color} strokeWidth={sw * 1.1} strokeLinecap="round" strokeLinejoin="round"
          />
        </>
      );
    }

    /* ── Docs: document with fold ── */
    case 'docs': {
      const m = s * 0.26;
      const t = s * 0.74;
      const fold = s * 0.12;
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw * 0.5} opacity={0.3} />
          {/* Page body */}
          <path
            d={`M${m + fold},${m} L${t},${m} L${t},${t} L${m},${t} L${m},${m + fold} Z`}
            fill={color} opacity={0.12} stroke={color} strokeWidth={sw * 0.9} strokeLinejoin="round"
          />
          {/* Fold triangle */}
          <path d={`M${m},${m + fold} L${m + fold},${m + fold} L${m + fold},${m}`}
            fill={bg} stroke={color} strokeWidth={sw * 0.7} strokeLinejoin="round" />
          {/* Text lines */}
          <line x1={cx - r * 0.1} y1={cy + s * 0.01} x2={t - s * 0.06} y2={cy + s * 0.01}
            stroke={color} strokeWidth={sw * 0.7} strokeLinecap="round" />
          <line x1={cx - r * 0.1} y1={cy + s * 0.11} x2={t - s * 0.12} y2={cy + s * 0.11}
            stroke={color} strokeWidth={sw * 0.7} strokeLinecap="round" />
        </>
      );
    }

    /* ── Integration: chain links ── */
    case 'integration': {
      const lr = r * 0.28;
      const gap = r * 0.12;
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw * 0.5} opacity={0.3} />
          {/* Left link */}
          <rect x={cx - lr * 2 - gap * 0.5} y={cy - lr * 0.65} width={lr * 2.4} height={lr * 1.3}
            rx={lr * 0.65} fill={color} opacity={0.15} stroke={color} strokeWidth={sw * 0.9} />
          {/* Right link */}
          <rect x={cx - lr * 0.4 + gap * 0.5} y={cy - lr * 0.65} width={lr * 2.4} height={lr * 1.3}
            rx={lr * 0.65} fill={color} opacity={0.15} stroke={color} strokeWidth={sw * 0.9} />
        </>
      );
    }

    /* ── Deploy: rocket ── */
    case 'deploy': {
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw * 0.5} opacity={0.3} />
          {/* Rocket body */}
          <path
            d={`M${cx},${cy - r * 0.6}
               C${cx + r * 0.25},${cy - r * 0.55} ${cx + r * 0.45},${cy - r * 0.1} ${cx + r * 0.45},${cy + r * 0.15}
               L${cx},${cy + r * 0.6}
               L${cx - r * 0.45},${cy + r * 0.15}
               C${cx - r * 0.45},${cy - r * 0.1} ${cx - r * 0.25},${cy - r * 0.55} ${cx},${cy - r * 0.6} Z`}
            fill={color} opacity={0.2} stroke={color} strokeWidth={sw * 0.8} strokeLinejoin="round"
          />
          {/* Window */}
          <circle cx={cx} cy={cy - r * 0.1} r={r * 0.14} fill={color} />
          {/* Flames */}
          <path
            d={`M${cx - r * 0.15},${cy + r * 0.55} L${cx},${cy + r * 0.8} L${cx + r * 0.15},${cy + r * 0.55}`}
            fill={color} opacity={0.5}
          />
        </>
      );
    }

    /* ── Done: filled circle with bold checkmark ── */
    case 'done':
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={color} />
          <path
            d={`M${cx - r * 0.35},${cy + r * 0.02} L${cx - r * 0.05},${cy + r * 0.32} L${cx + r * 0.38},${cy - r * 0.25}`}
            fill="none" stroke="white" strokeWidth={sw * 1.5} strokeLinecap="round" strokeLinejoin="round"
          />
        </>
      );

    /* ── Cancelled: filled gray with X ── */
    case 'cancelled':
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={color} opacity={0.7} />
          <line x1={cx - r * 0.3} y1={cy - r * 0.3} x2={cx + r * 0.3} y2={cy + r * 0.3}
            stroke="white" strokeWidth={sw * 1.3} strokeLinecap="round" />
          <line x1={cx + r * 0.3} y1={cy - r * 0.3} x2={cx - r * 0.3} y2={cy + r * 0.3}
            stroke="white" strokeWidth={sw * 1.3} strokeLinecap="round" />
        </>
      );

    /* ── Blocked: red filled with stop bar ── */
    case 'blocked':
      return (
        <>
          <circle cx={cx} cy={cy} r={r} fill={color} />
          <line x1={cx - r * 0.4} y1={cy} x2={cx + r * 0.4} y2={cy}
            stroke="white" strokeWidth={sw * 1.6} strokeLinecap="round" />
        </>
      );

    /* ── Default: half-moon fill ── */
    default:
      return (
        <>
          <defs>
            <clipPath id={`half-${uid}`}>
              <rect x={cx} y={0} width={cx} height={s} />
            </clipPath>
          </defs>
          <circle cx={cx} cy={cy} r={r} fill={bg} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw} />
          <circle cx={cx} cy={cy} r={r} fill={color} opacity={0.3} clipPath={`url(#half-${uid})`} />
        </>
      );
  }
};

/* ── Component ── */
interface Props {
  status: string;
  size?: number;
}

let uidCounter = 0;

export const StatusIcon: React.FC<Props> = ({ status, size = 14 }) => {
  const def = findStatus(status);
  const uid = `si-${++uidCounter}`;

  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} $pulse={def.pulse} $spin={def.spin}>
      {renderIcon(def.icon, def.color, def.bg, size, uid)}
    </Svg>
  );
};

export const getStatusColor = (status: string) => findStatus(status).color;

/**
 * True when a column name means "finished" (Готово / Done / Завершено / Закрыт…).
 * Kept next to the status table so callers don't re-implement the naming rules:
 * a card sitting in such a column is done, so its due date must stop shouting
 * about an approaching or missed deadline.
 */
export const isCompletedStatus = (status?: string | null) =>
  !!status && findStatus(status).icon === 'done';
